/**
 * Canonical + hreflang for a bilingual page - `EX-PUB02`.
 *
 * Every public page pair on this site is the *same document in two languages*,
 * which is exactly what hreflang describes, and getting it right is worth one
 * helper rather than eleven hand-written `languages` objects that drift.
 *
 * Three rules it encodes, all of them things that are easy to get subtly wrong:
 *
 * - **The pair is reciprocal.** `/en/judge/x` names `/bn/judge/x` and vice
 *   versa, and each names itself. A one-way annotation is ignored by Google.
 * - **`x-default` points at the default locale**, not at a locale-less URL -
 *   this app has no unprefixed route to point at (`/judge/x` redirects), and an
 *   `x-default` that 3xx's is worse than none.
 * - **A page that must not be indexed gets no alternates at all**, which is why
 *   this returns a value the caller spreads rather than something applied by
 *   default. `docs/29` §5's `unlisted` contest is the case: annotating its
 *   Bangla twin is an invitation to crawl the thing whose whole point is not
 *   being listed. `noIndexMetadata` below is the other half of that decision.
 */
import type { Metadata } from "next";
import { defaultLocale, locales, type Locale } from "@/lib/i18n";

export type LocaleAlternates = NonNullable<Metadata["alternates"]>;

function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

/**
 * @param locale the locale being rendered, used for the canonical
 * @param path   locale-less and leading-slashed, e.g. `/judge/two-sum`
 */
export function localeAlternates(locale: string, path: string): LocaleAlternates {
  const current = isLocale(locale) ? locale : defaultLocale;
  const languages: Record<string, string> = {};
  for (const other of locales) {
    languages[other] = `/${other}${path}`;
  }
  languages["x-default"] = `/${defaultLocale}${path}`;

  return {
    canonical: `/${current}${path}`,
    languages,
  };
}

/**
 * What a page emits when it must not be indexed.
 *
 * Deliberately **not** merged with `localeAlternates` - a page is either in the
 * index with a full hreflang pair or out of it with none, and a helper that
 * could produce "noindex plus alternates" would make that a choice at eleven
 * call sites instead of a rule.
 */
export const noIndexMetadata = {
  robots: { index: false, follow: false },
} as const satisfies Pick<Metadata, "robots">;
